import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import {
  unitsApi,
  UNIT_MARK_ACTIVITY_READ_KEY,
  UNIT_MARK_VIEWED_KEY
} from "api/units";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { getErrorMessage } from "utils/error";
import { LeadActivity, LeadActivityType } from "views/leads-id/types";
import { ActivityChange } from "views/properties-id/slices/ActivityLog/types";

const UNIT_ACTIVITY_LOG_KEY = "UNIT_ACTIVITY_LOG";

export type ActivityCategory =
  | "leads"
  | "showings"
  | "applications"
  | "messages"
  | "details"
  | "pms_sync";

export interface UnitActivityRow {
  id: string;
  activity_log_id?: number;
  actor_type: string | null;
  actor_name: string | null;
  pms_type?: string | null;
  occurred_at: string | null;
  bundle_type: string;
  category: ActivityCategory;
  event_type?: LeadActivityType;
  metadata?: LeadActivity["metadata"];
  summary: string | null;
  description?: string | null;
  changes?: ActivityChange[];
  read: boolean | null;
  lead?: { id: number; name: string | null } | null;
  navigate_to?: { type: string; lead_id: number } | null;
}

interface UnitActivityResponse {
  activity_log: UnitActivityRow[];
  category_counts: Record<string, number>;
  unread_count: number;
  pagination: {
    page: number;
    per_page: number;
    total: number;
    truncated: boolean;
  };
}

export const useUnitActivityLog = ({
  propertyId,
  unitId
}: {
  propertyId?: number;
  unitId?: number;
}) => {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<ActivityCategory | "all">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [debouncedSearch] = useDebouncedValue(search, 400);

  const enabled = Boolean(propertyId && unitId);

  const { data, isFetching } = useQuery({
    queryKey: [
      UNIT_ACTIVITY_LOG_KEY,
      propertyId,
      unitId,
      category,
      debouncedSearch,
      page
    ],
    queryFn: () =>
      instance
        .get<UnitActivityResponse>(
          `/portal/v1/properties/${propertyId}/activity_log`,
          {
            params: {
              unit_id: unitId,
              category: category === "all" ? undefined : category,
              search: debouncedSearch || undefined,
              page,
              per_page: 20
            }
          }
        )
        .then((res) => res.data),
    enabled,
    placeholderData: keepPreviousData,
    meta: { onError: (error: unknown) => toast.error(getErrorMessage(error)) }
  });

  // The units list carries the same unread count on its bell badge, so clearing
  // a row here has to drop that cache too or the badge keeps the old number.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [UNIT_ACTIVITY_LOG_KEY] });
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        typeof queryKey[0] === "string" && queryKey[0].includes("/units")
    });
  };

  const markAllRead = useMutation({
    mutationKey: [UNIT_MARK_VIEWED_KEY, unitId, "UPDATE"],
    mutationFn: () => unitsApi.markViewed(Number(unitId)),
    onSuccess: invalidate,
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const markRowRead = useMutation({
    mutationKey: [UNIT_MARK_ACTIVITY_READ_KEY, unitId, "UPDATE"],
    mutationFn: (activityLogId: number) =>
      unitsApi.markActivityRead(Number(unitId), activityLogId),
    onSuccess: invalidate,
    onError: (error) => toast.error(getErrorMessage(error))
  });

  return {
    rows: data?.activity_log ?? [],
    categoryCounts: data?.category_counts ?? {},
    unreadCount: data?.unread_count ?? 0,
    pageCount: Math.ceil(
      (data?.pagination.total ?? 0) / (data?.pagination.per_page || 20)
    ),
    page,
    setPage,
    category,
    setCategory,
    search,
    setSearch,
    isFetching,
    markAllRead,
    markRowRead
  };
};
